import { ArrowBackOutline } from '@styled-icons/evaicons-outline/ArrowBackOutline'
import * as React from 'react'
import styled from 'styled-components'

import type { Question } from '@app/domain/instrument'
import type { IEntry } from '@app/domain/instrument/instrument.interface'

import {
  FooterScrollTextContainer,
  FormQuestions,
  HeaderContainer,
  ScreenContent,
  InstrumentScreenContent,
} from './content-layout'
import { BackButton, ContinueButton, Control, ScrollDown } from './controls'
import { FormGroup, Radio } from './form-inputs'
import { TextQuestion } from './input/text-question'

export const ConsentFooterRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 1rem;
  padding-bottom: 4rem;
  position: sticky;
  bottom: 0;
  z-index: 1000;
  border-radius: 25px;
  background-color: white;
`

export const ConsentControlButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 0;
`

const ScreenHeader = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
  font-size: 16pt;
  font-weight: 500;
`

interface IntakeConsentProps {
  header?: string
  entry?: IEntry
  instrumentId: string
  content?: string
  questions: Question[]
  handleGoBack?: () => void
  handleContinue?: () => void
  handleSubmit?: (
    instrumentId: string,
    responses: Record<string, string>,
  ) => void
}

interface IntakeConsentState {
  responses: Record<string, string>
  displayScrollBottom: boolean
  displayScrollTop: boolean
}

export class IntakeConsent extends React.Component<
  IntakeConsentProps,
  IntakeConsentState
> {
  constructor(props) {
    super(props)
    this.state = {
      responses: this.props.entry ? this.props.entry.responses : {},
      displayScrollBottom: false,
      displayScrollTop: false,
    }
  }

  componentDidMount() {
    this.setState({
      responses: this.props.entry ? this.props.entry.responses : {},
    })
    const pageBackground = document.querySelector('.page-background')
    if (pageBackground) {
      pageBackground.addEventListener('scroll', this.handleScroll)
      this.handleScroll()
    }
  }

  componentWillUnmount() {
    const pageBackground = document.querySelector('.page-background')
    if (pageBackground) {
      pageBackground.removeEventListener('scroll', this.handleScroll)
    }
  }

  handleScroll = () => {
    const scrollParent = document.querySelector('.page-background')
    const { scrollTop, scrollHeight, clientHeight } = scrollParent
    const atBottom = Math.abs(scrollHeight - scrollTop) - clientHeight <= 1

    this.setState({
      displayScrollBottom: !atBottom,
    })
  }

  componentDidUpdate(nextProps) {
    const { entry } = this.props
    if (nextProps.entry !== entry) {
      this.setState({
        responses: this.props.entry ? this.props.entry.responses : {},
      })
    }
  }

  handleResponseChange = (question: string, value: string) => {
    this.setState({
      responses: {
        ...this.state.responses,
        [question]: value,
      },
    })
  }

  renderBackButton = (goBack?: () => void) => {
    if (goBack) {
      return (
        <BackButton onClick={goBack}>
          <ArrowBackOutline size="45" />
        </BackButton>
      )
    } else {
      return <></>
    }
  }

  renderHeader = (header?: string) => {
    if (!header) {
      return null
    }

    return (
      <HeaderContainer>
        <ScreenHeader dangerouslySetInnerHTML={{ __html: header }} />
      </HeaderContainer>
    )
  }

  renderContent = (content?: string) => {
    if (content) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />
    } else {
      return <></>
    }
  }

  renderQuestion = (question: Question, key: number) => {
    switch (question.inputType) {
      case 'text':
        return (
          <TextQuestion
            key={question.id}
            question={question}
            value={this.state.responses[question.id]}
            onChange={(value) => {
              this.handleResponseChange(question.id, value)
            }}
          />
        )
      case 'radio': {
        return (
          <FormGroup
            key={`question-${key}`}
            fieldId={question.id}
            label={question.label}
          >
            {question.options.map((option) => (
              <Radio
                id={`${question.id}_${option.value}`}
                key={`${question.id}_${option.value}`}
                label={option.label}
                checked={this.state.responses[question.id] === option.value}
                name={question.id}
                value={option.value}
                onChange={(event) => {
                  this.handleResponseChange(
                    question.id,
                    event.currentTarget.value,
                  )
                }}
              />
            ))}
          </FormGroup>
        )
      }
      default:
        return <React.Fragment key={key} />
    }
  }

  isValid = (questions): boolean => {
    return questions.every((question) => {
      if (question.required && !this.state.responses[question.id]) {
        return false
      } else {
        return true
      }
    })
  }

  renderContinueButton = (questions) => {
    if (this.props.handleSubmit && this.isValid(questions)) {
      return (
        <ContinueButton
          onClick={() =>
            this.props.handleSubmit(this.props.entry.id, this.state.responses)
          }
        >
          Continue
        </ContinueButton>
      )
    } else {
      return <></>
    }
  }

  render() {
    const { handleGoBack, header, content, questions } = this.props
    return (
      <ScreenContent>
        {this.renderHeader(header)}

        <InstrumentScreenContent>
          {this.renderContent(content)}
        </InstrumentScreenContent>
        <ConsentFooterRow>
          <FormQuestions>
            {questions.map((question, idx) =>
              this.renderQuestion(question, idx),
            )}
          </FormQuestions>
          <ConsentControlButtonsContainer>
            <Control>{this.renderBackButton(handleGoBack)}</Control>
            <Control>{this.renderContinueButton(questions)}</Control>
            <Control />
          </ConsentControlButtonsContainer>
          {this.state.displayScrollBottom ? (
            <FooterScrollTextContainer>
              <ScrollDown />
              Scroll Down for More
            </FooterScrollTextContainer>
          ) : null}
        </ConsentFooterRow>
      </ScreenContent>
    )
  }
}
